import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    User,
    X,
    ChevronDown,
    ChevronRight,
    Menu,
    Globe,
    Mail,
    Phone,
    MonitorCheck,
    ExternalLink,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import GoogleTranslate from "../../google-translate/GoogleTranslate";
import { courseApi, noticeApi } from "../../api/api";
import api from "../../utils/api";
import LanguagePopup from "../LanguagePopup";
import { useAuth } from "../../utils/AuthContext";
import { getTestPortalUrl, buildSsoUrl, getLogoutUrl } from "../../utils/sso";

// ==========================================
// INTERNAL COMPONENTS
// ==========================================

const DesktopDropdown = ({
    title,
    isActive,
    onMouseEnter,
    onMouseLeave,
    children,
}) => {
    return (
        <div
            className="relative"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="cursor-pointer text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 select-none">
                <span>{title}</span>
                <ChevronDown
                    size={14}
                    className={`ml-1.5 transition-transform duration-300 ${isActive ? "rotate-180 text-red-700" : ""}`}
                />
            </div>
            <AnimatePresence>
                {isActive && (
                    <motion.div
                        initial={{ opacity: 0, y: 15, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{
                            type: "spring",
                            stiffness: 200,
                            damping: 20,
                        }}
                        className="absolute top-full left-0 pt-4 w-52 z-50"
                    >
                        <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] py-2 border border-gray-100 relative">
                            <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                            <div className="relative z-10">{children}</div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const MobileAccordion = ({ title, isActive, onClick, children }) => {
    return (
        <div className="w-full flex flex-col mb-2">
            <button
                onClick={onClick}
                className="w-full flex items-center justify-between py-3.5 px-4 bg-white rounded-xl text-neutral-900 font-bold shadow-sm transition-colors"
            >
                <span>{title}</span>
                <ChevronDown
                    size={16}
                    className={`transition-transform duration-300 ${isActive ? "rotate-180 text-red-700" : ""}`}
                />
            </button>
            <AnimatePresence>
                {isActive && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="flex flex-col pl-4 pt-2 gap-1 border-l-2 border-red-100 ml-4 mt-1">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// ==========================================
// MAIN NAVBAR COMPONENT
// ==========================================

const Navbar = () => {
    const [showLangPopup, setShowLangPopup] = useState(false);
    const [pages, setPages] = useState([]);
    const [offers, setOffers] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [loading, setLoading] = useState(true);

    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const isLoggedIn = Boolean(user?.token);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [noticeRes, courseRes] = await Promise.all([
                    api
                        .get(`${noticeApi}`)
                        .catch(() => ({ data: { success: false } })),
                    api.get(`${courseApi}`).catch(() => ({ data: [] })),
                ]);

                if (noticeRes.data?.success)
                    setOffers(noticeRes.data.data || []);

                const uniquePages = [];
                (courseRes.data || []).forEach((course) => {
                    if (!uniquePages.some((p) => p.name === course.name)) {
                        uniquePages.push({
                            name: course.name,
                            id: course.id,
                            parent_id: course.parent_id,
                        });
                    }
                });
                setPages(uniquePages);
            } catch (err) {
                console.error("API Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (offers.length > 1) {
            const interval = setInterval(
                () => setCurrentIndex((prev) => (prev + 1) % offers.length),
                3000,
            );
            return () => clearInterval(interval);
        }
    }, [offers]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = () => {
        try {
            api.post("/logout");
        } catch {}
        logout();
        setDropdownOpen(false);
        const loginUrl = `${window.location.origin}/login`;
        window.location.assign(getLogoutUrl(loginUrl));
    };

    const handleTestPortal = (e) => {
        e.preventDefault();
        const url = isLoggedIn
            ? buildSsoUrl(user.token, "/")
            : getTestPortalUrl("/");
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const navLinkClass = ({ isActive }) =>
        isActive
            ? "relative underline underline-offset-4 text-black font-bold px-4 py-2 flex items-center transition-all duration-300 transform scale-105"
            : "relative text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 whitespace-nowrap group";

    const dropdownItemClass = ({ isActive }) =>
        isActive
            ? "block px-5 py-3 text-sm font-bold bg-red-50 text-red-700 transition-colors border-l-4 border-red-600"
            : "block px-5 py-3 text-sm font-semibold text-neutral-800 hover:bg-red-50 hover:text-red-700 hover:translate-x-1 transition-all duration-200 border-l-4 border-transparent";

    const mobileNavItemClass = ({ isActive }) =>
        isActive
            ? "block py-2.5 px-3 rounded-lg text-sm font-bold bg-red-50 text-red-700"
            : "block py-2.5 px-3 rounded-lg text-sm font-semibold text-neutral-700 hover:bg-red-50 hover:text-red-700 transition-colors";

    return (
        <>
            <style>{`
                @keyframes smoothMarquee {
                    0% { transform: translateX(100%); }
                    100% { transform: translateX(-100%); }
                }
                .css-marquee {
                    display: inline-block;
                    white-space: nowrap;
                    animation: smoothMarquee 20s linear infinite;
                }
                .css-marquee:hover { animation-play-state: paused; }
                
                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateX(10px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .sub-menu-animate {
                    opacity: 0;
                    animation: fadeSlideIn 0.2s ease-out forwards;
                }
            `}</style>

            {/* Topbar */}
            <div className="bg-neutral-900 w-full relative z-[60] border-b border-neutral-800 overflow-hidden">
                <div className="max-w-[1440px] mx-auto w-full text-gray-300 text-[11px] md:text-xs py-1.5 md:py-2 px-3 sm:px-6 lg:px-8 flex flex-row justify-between items-center gap-2 md:gap-4">
                    <div className="flex items-center space-x-2 sm:space-x-3 font-medium lowercase tracking-wide shrink-0">
                        <a
                            href="mailto:upscpyqpedia@gmail.com"
                            className="hover:text-yellow-400 transition-colors flex items-center gap-1.5"
                            title="Email"
                        >
                            <Mail size={12} />
                            <span className="hidden sm:inline">
                                upscpyqpedia@gmail.com
                            </span>
                        </a>
                        <span className="text-neutral-600">|</span>
                        <a
                            href="tel:+918709364253"
                            className="hover:text-yellow-400 transition-colors flex items-center gap-1.5"
                            title="Call Us"
                        >
                            <Phone size={12} />
                            <span className="hidden xs:inline">
                                +91 8709364253
                            </span>
                        </a>
                    </div>

                    <div className="flex-1 max-w-[200px] xs:max-w-xs md:max-w-xl h-5 md:h-6 flex items-center overflow-hidden relative mx-1">
                        {loading ? (
                            <span className="text-neutral-500 animate-pulse text-center w-full block text-[10px] md:text-xs truncate">
                                Checking updates...
                            </span>
                        ) : offers.length > 0 ? (
                            <div className="w-full relative overflow-hidden h-full">
                                <div className="css-marquee h-full flex items-center">
                                    <a
                                        href={offers[currentIndex]?.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-gray-200 hover:text-yellow-400 font-semibold tracking-wide transition-colors px-2 text-[10px] md:text-xs"
                                    >
                                        {offers[currentIndex]?.title}
                                    </a>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="shrink-0 flex items-center">
                        <button
                            className="md:hidden flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 active:bg-neutral-700 px-2 py-0.5 rounded-full text-gray-200 border border-neutral-700 transition-colors"
                            onClick={() => {
                                setShowLangPopup(true);
                                setMobileMenuOpen(false);
                            }}
                            aria-label="Change Language"
                        >
                            <Globe size={11} className="text-yellow-400" />
                            <span className="text-[10px] font-semibold tracking-tight">
                                Lang
                            </span>
                        </button>
                        <button
                            className="hidden md:flex items-center gap-1.5 text-gray-300 transition-colors font-medium tracking-wide hover:text-white"
                            onClick={() => setShowLangPopup(true)}
                        >
                            <Globe size={14} /> Change Language
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Navbar */}
            <nav
                className={`sticky top-0 z-[50] w-full border-b transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-xl border-gray-100 shadow-sm py-2.5" : "bg-yellow-400 border-transparent shadow-sm py-3"}`}
            >
                <div className="max-w-[1440px] mx-auto w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6">
                    <Link
                        to="/"
                        className="flex items-center gap-3 shrink-0 cursor-pointer select-none group"
                    >
                        <img
                            src="/assets/logo.png"
                            alt="PYQ Pedia"
                            className={`object-contain transition-all duration-300 ${isScrolled ? "w-10 h-10 sm:w-12 sm:h-12" : "w-12 h-12 sm:w-14 sm:h-14"}`}
                        />
                        <div className="hidden sm:block leading-none">
                            <p
                                className={`font-black tracking-tight transition-colors ${isScrolled ? "text-neutral-900 text-[15px]" : "text-neutral-900 text-[16px]"}`}
                            >
                                PYQ PEDIA
                            </p>
                            <p
                                className={` font-medium transition-colors ${isScrolled ? "text-neutral-700 text-[11px]" : "text-neutral-800 text-[12px]"}`}
                            >
                                Master The Past, Ace The Future
                            </p>
                        </div>
                    </Link>

                    {/* Desktop Navigation Links - Changed to xl breakpoint to prevent horizontal overflow on laptops */}
                    <div className="hidden xl:flex items-center justify-center flex-1 gap-1 2xl:gap-2">
                        <div
                            className="relative"
                            onMouseEnter={() => setOpenDropdown("courses")}
                            onMouseLeave={() => setOpenDropdown(null)}
                        >
                            <div className="cursor-pointer text-neutral-900 font-semibold px-4 py-2 flex items-center hover:bg-white hover:text-red-700 transition-all duration-300 select-none">
                                <span>Courses</span>
                                <ChevronDown
                                    size={14}
                                    className={`ml-1.5 transition-transform duration-300 ${openDropdown === "courses" ? "rotate-180 text-red-700" : ""}`}
                                />
                            </div>
                            <AnimatePresence>
                                {openDropdown === "courses" && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute top-full left-0 pt-4 w-64 z-50"
                                    >
                                        <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] py-2 border border-gray-100 relative">
                                            <div className="absolute -top-2 left-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                            <div className="relative z-10 flex flex-col">
                                                {pages
                                                    .filter(
                                                        (p) =>
                                                            p &&
                                                            p.parent_id ===
                                                                null,
                                                    )
                                                    .map((pageObj) => {
                                                        const children =
                                                            pages.filter(
                                                                (p) =>
                                                                    p.parent_id ===
                                                                    pageObj.id,
                                                            );
                                                        return (
                                                            <div
                                                                key={pageObj.id}
                                                                className="relative group/sub"
                                                            >
                                                                <NavLink
                                                                    to={`/course/course-page/${pageObj.id}`}
                                                                    className="px-5 py-3 text-sm font-semibold text-neutral-800 hover:bg-red-50 hover:text-red-700 flex items-center justify-between w-full transition-all duration-200 border-l-4 border-transparent hover:border-red-600"
                                                                >
                                                                    {
                                                                        pageObj.name
                                                                    }
                                                                    {children.length >
                                                                        0 && (
                                                                        <ChevronRight
                                                                            size={
                                                                                14
                                                                            }
                                                                            className="text-gray-400 group-hover/sub:text-red-700"
                                                                        />
                                                                    )}
                                                                </NavLink>

                                                                {/* Fixed Invisible Bounding Box Issue */}
                                                                {children.length >
                                                                    0 && (
                                                                    <div className="absolute top-0 left-[95%] pl-2 w-56 z-[60] hidden group-hover/sub:block">
                                                                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 py-2 flex flex-col sub-menu-animate">
                                                                            {children.map(
                                                                                (
                                                                                    child,
                                                                                ) => (
                                                                                    <NavLink
                                                                                        key={
                                                                                            child.id
                                                                                        }
                                                                                        to={`/course/course-page/${child.id}`}
                                                                                        className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-700 transition-all duration-200"
                                                                                    >
                                                                                        {
                                                                                            child.name
                                                                                        }
                                                                                    </NavLink>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* <NavLink to="/test-series" className={navLinkClass}>
                            Test Series
                        </NavLink> */}
                        <NavLink to="/free-initiative" className={navLinkClass}>
                            Free Initiative
                        </NavLink>

                        <DesktopDropdown
                            title="Vacancies"
                            isActive={openDropdown === "vacancy"}
                            onMouseEnter={() => setOpenDropdown("vacancy")}
                            onMouseLeave={() => setOpenDropdown(null)}
                        >
                            <NavLink
                                to="/recent-vacancies/upsc"
                                className={dropdownItemClass}
                            >
                                UPSC
                            </NavLink>
                            <NavLink
                                to="/recent-vacancies/state-psc"
                                className={dropdownItemClass}
                            >
                                State PSC
                            </NavLink>
                            <NavLink
                                to="/recent-vacancies/other"
                                className={dropdownItemClass}
                            >
                                Other
                            </NavLink>
                        </DesktopDropdown>

                        <DesktopDropdown
                            title="Resources"
                            isActive={openDropdown === "resources"}
                            onMouseEnter={() => setOpenDropdown("resources")}
                            onMouseLeave={() => setOpenDropdown(null)}
                        >
                            <NavLink
                                to="/test-series"
                                className={dropdownItemClass}
                            >
                                Mock Tests
                            </NavLink>
                            <NavLink
                                to="/downloads"
                                className={dropdownItemClass}
                            >
                                Downloads
                            </NavLink>
                            <NavLink
                                to="/current-affairs"
                                className={dropdownItemClass}
                            >
                                Current Affairs
                            </NavLink>
                            <NavLink to="/blogs" className={dropdownItemClass}>
                                Blogs
                            </NavLink>
                        </DesktopDropdown>
                    </div>

                    {/* Desktop CTA & Auth - Switched to xl */}
                    <div className="hidden xl:flex items-center gap-4 shrink-0">
                        <a
                            href={getTestPortalUrl("/")}
                            onClick={handleTestPortal}
                            className="relative inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-b from-neutral-800 to-neutral-950 text-white text-sm font-semibold rounded-full overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group border border-neutral-700 hover:border-yellow-500/50"
                        >
                            <div className="absolute inset-0 -translate-x-[150%] bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:translate-x-[150%] transition-transform duration-1000 ease-in-out" />
                            <MonitorCheck
                                size={16}
                                className="text-yellow-400 relative z-10"
                            />
                            <span className="relative z-10 tracking-wide text-gray-100 group-hover:text-white transition-colors">
                                Test Portal
                            </span>
                        </a>

                        {isLoggedIn ? (
                            <div className="relative pl-1" ref={dropdownRef}>
                                <div
                                    className="relative cursor-pointer select-none group"
                                    onClick={() =>
                                        setDropdownOpen(!isDropdownOpen)
                                    }
                                >
                                    <div className="absolute -inset-0.5 rounded-full bg-gradient-to-tr from-yellow-400 to-amber-600 opacity-0 group-hover:opacity-60 blur-sm transition-opacity duration-300" />
                                    <div className="relative w-11 h-11 rounded-full bg-gradient-to-br from-neutral-800 to-neutral-950 flex items-center justify-center font-black text-lg text-white shadow-inner border border-neutral-700 group-hover:border-neutral-900 transition-all duration-300">
                                        {user?.name ? (
                                            user.name[0].toUpperCase()
                                        ) : (
                                            <User size={20} />
                                        )}
                                    </div>
                                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full shadow-sm z-10"></span>
                                </div>

                                <AnimatePresence>
                                    {isDropdownOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 15 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 10 }}
                                            className="absolute top-full right-0 pt-4 w-56 z-[60]"
                                        >
                                            <div className="bg-white rounded-2xl shadow-xl py-3 border border-gray-100 flex flex-col gap-1.5 px-3 relative">
                                                <div className="absolute -top-2 right-5 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100 z-0"></div>
                                                <div className="relative z-10 flex flex-col">
                                                    <div className="px-3 py-2 mb-2 border-b border-gray-100">
                                                        <p className="text-xs text-gray-500 font-medium">
                                                            Logged in as
                                                        </p>
                                                        <p className="text-sm font-bold text-neutral-900 truncate">
                                                            {user?.name}
                                                        </p>
                                                    </div>
                                                    <Link
                                                        to="/dashboard"
                                                        className="bg-yellow-50 text-yellow-800 font-bold text-sm py-2.5 px-4 rounded-xl hover:bg-yellow-100 transition-colors"
                                                    >
                                                        My Dashboard
                                                    </Link>
                                                    <Link
                                                        to="/change-password"
                                                        className="bg-gray-50 text-gray-700 font-bold text-sm py-2.5 px-4 rounded-xl hover:bg-gray-100 transition-colors"
                                                    >
                                                        Change Password

                                                    </Link>
                                                    <button
                                                        onClick={handleLogout}
                                                        className="bg-red-50 text-red-700 font-bold text-sm py-2.5 px-4 rounded-xl hover:bg-red-100 text-left mt-1"
                                                    >
                                                        Sign Out
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 pl-3 border-l border-neutral-300">
                                <Link
                                    to="/login"
                                    className="text-neutral-800 font-bold text-sm hover:text-black transition-colors px-2"
                                >
                                    Log In
                                </Link>
                                <Link
                                    to="/register"
                                    className="px-5 py-2.5 bg-neutral-900 text-white text-sm font-bold rounded-full shadow-md hover:bg-black transition-all"
                                >
                                    Sign Up
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Toggle - Switched to xl */}
                    <div className="xl:hidden flex items-center gap-2 shrink-0">
                        <a
                            href={getTestPortalUrl("/")}
                            onClick={handleTestPortal}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-b from-neutral-800 to-neutral-950 text-white text-xs font-bold rounded-full shadow border border-neutral-700 hover:border-yellow-500/50 transition-all"
                        >
                            <MonitorCheck
                                size={13}
                                className="text-yellow-400"
                            />
                            <span>Portal</span>
                        </a>
                        <button
                            className="flex items-center justify-center w-9 h-9 bg-white rounded-full shadow-sm text-neutral-900 border border-gray-100"
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <Menu size={18} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-neutral-900/40 backdrop-blur-sm z-[100]"
                            onClick={() => setMobileMenuOpen(false)}
                        ></motion.div>
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{
                                type: "spring",
                                damping: 25,
                                stiffness: 200,
                            }}
                            className="fixed inset-y-0 right-0 w-full max-w-[320px] bg-slate-50 shadow-2xl z-[101] flex flex-col overflow-hidden"
                        >
                            <div className="bg-neutral-900 p-6 flex flex-col items-center relative">
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="absolute top-4 left-4 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-white"
                                >
                                    <X size={18} />
                                </button>
                                {isLoggedIn ? (
                                    <>
                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neutral-800 to-neutral-950 text-yellow-500 flex items-center justify-center font-black text-2xl mb-3 border border-neutral-700 shadow-inner relative">
                                            {user?.name ? (
                                                user.name[0].toUpperCase()
                                            ) : (
                                                <User
                                                    size={28}
                                                    className="text-gray-400"
                                                />
                                            )}
                                            <span className="absolute bottom-1 right-1 w-3 h-3 bg-green-500 border-2 border-neutral-900 rounded-full"></span>
                                        </div>
                                        <h3 className="text-white font-bold">
                                            {user?.name}
                                        </h3>
                                    </>
                                ) : (
                                    <div className="mt-4 mb-1 flex flex-col items-center w-full">
                                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-3">
                                            <img
                                                src="/assets/logo.png"
                                                alt="PYQ Pedia"
                                                className="w-10 h-10 object-contain brightness-0 invert opacity-80"
                                            />
                                        </div>
                                        <h3 className="text-white font-bold text-lg">
                                            PYQ Pedia
                                        </h3>
                                        <p className="text-gray-400 text-xs mt-1">
                                            Smart Test Series & Courses
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                                <a
                                    href={getTestPortalUrl("/")}
                                    onClick={(e) => {
                                        handleTestPortal(e);
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center justify-between py-3.5 px-4 bg-gradient-to-r from-neutral-900 to-neutral-800 border border-yellow-500/40 text-yellow-400 rounded-xl font-bold shadow-sm mb-3 group"
                                >
                                    <div className="flex items-center gap-2.5 text-white">
                                        <MonitorCheck
                                            size={18}
                                            className="text-yellow-400"
                                        />
                                        <span className="text-sm tracking-wide">
                                            Test Portal
                                        </span>
                                    </div>
                                    <ChevronRight
                                        size={16}
                                        className="text-yellow-400 group-hover:translate-x-0.5 transition-transform"
                                    />
                                </a>

                                <MobileAccordion
                                    title="Courses"
                                    isActive={openMobileDropdown === "courses"}
                                    onClick={() =>
                                        setOpenMobileDropdown(
                                            openMobileDropdown === "courses"
                                                ? null
                                                : "courses",
                                        )
                                    }
                                >
                                    {pages
                                        .filter((p) => p && p.parent_id === null)
                                        .map((pageObj) => {
                                            const children = pages.filter(
                                                (p) => p.parent_id === pageObj.id,
                                            );
                                            return children.length > 0 ? (
                                                <MobileAccordion
                                                    key={pageObj.id}
                                                    title={pageObj.name}
                                                    isActive={
                                                        openMobileDropdown ===
                                                            `courses-${pageObj.id}`
                                                    }
                                                    onClick={() =>
                                                        setOpenMobileDropdown(
                                                            openMobileDropdown ===
                                                                `courses-${pageObj.id}`
                                                                ? null
                                                                : `courses-${pageObj.id}`,
                                                        )
                                                    }
                                                >
                                                    {children.map((child) => (
                                                        <NavLink
                                                            key={child.id}
                                                            to={`/course/course-page/${child.id}`}
                                                            className={mobileNavItemClass}
                                                            onClick={() => setMobileMenuOpen(false)}
                                                        >
                                                            {child.name}
                                                        </NavLink>
                                                    ))}
                                                </MobileAccordion>
                                            ) : (
                                                <NavLink
                                                    key={pageObj.id}
                                                    to={`/course/course-page/${pageObj.id}`}
                                                    className={mobileNavItemClass}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    {pageObj.name}
                                                </NavLink>
                                            );
                                        })
                                    }
                                </MobileAccordion>

                                <NavLink
                                    to="/free-initiative"
                                    className="w-full flex items-center py-3.5 px-4 bg-white rounded-xl text-neutral-900 font-bold shadow-sm mt-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Free Initiative
                                </NavLink>

                                <MobileAccordion
                                    title="Vacancies"
                                    isActive={
                                        openMobileDropdown === "vacancies"
                                    }
                                    onClick={() =>
                                        setOpenMobileDropdown(
                                            openMobileDropdown === "vacancies"
                                                ? null
                                                : "vacancies",
                                        )
                                    }
                                >
                                    <NavLink
                                        to="/recent-vacancies/upsc"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        UPSC
                                    </NavLink>
                                    <NavLink
                                        to="/recent-vacancies/state-psc"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        State PSC
                                    </NavLink>
                                    <NavLink
                                        to="/recent-vacancies/other"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Other
                                    </NavLink>
                                </MobileAccordion>

                                <MobileAccordion
                                    title="Resources"
                                    isActive={
                                        openMobileDropdown === "resources"
                                    }
                                    onClick={() =>
                                        setOpenMobileDropdown(
                                            openMobileDropdown === "resources"
                                                ? null
                                                : "resources",
                                        )
                                    }
                                >
                                    <NavLink
                                        to="/test-series"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Mock Tests
                                    </NavLink>
                                    <NavLink
                                        to="/downloads"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Downloads
                                    </NavLink>
                                    <NavLink
                                        to="/current-affairs"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Current Affairs
                                    </NavLink>
                                    <NavLink
                                        to="/blogs"
                                        className={mobileNavItemClass}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Blogs
                                    </NavLink>
                                </MobileAccordion>

                            </div>

                            <div className="p-4 bg-white border-t border-gray-100 shadow-md">
                                {isLoggedIn ? (
                                    <button
                                        onClick={handleLogout}
                                        className="w-full bg-red-50 text-red-600 font-bold py-3 rounded-xl"
                                    >
                                        Sign Out
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <Link
                                            to="/login"
                                            className="flex-1 block bg-gray-100 text-neutral-900 font-bold py-3 rounded-xl text-center border border-gray-200 text-sm"
                                            onClick={() =>
                                                setMobileMenuOpen(false)
                                            }
                                        >
                                            Log In
                                        </Link>
                                        <Link
                                            to="/register"
                                            className="flex-1 block bg-gradient-to-b from-neutral-800 to-neutral-950 text-white font-bold py-3 rounded-xl text-center text-sm"
                                            onClick={() =>
                                                setMobileMenuOpen(false)
                                            }
                                        >
                                            Sign Up
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {showLangPopup && (
                <LanguagePopup onClose={() => setShowLangPopup(false)} />
            )}
            <div style={{ display: "none" }}>
                <GoogleTranslate />
            </div>

            <div className="fixed bottom-6 right-6 z-[90]">
                <a
                    href="https://wa.me/918709364253"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center w-14 h-14 bg-green-500 rounded-full shadow-2xl hover:-translate-y-1.5 transition-transform"
                >
                    <img
                        src="/assets/whatsapp-brands-solid-full.svg"
                        alt="WhatsApp"
                        className="w-7 h-7"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                </a>
            </div>
        </>
    );
};

export default Navbar;
